import { useRadialToolSelector } from "@frontend/pages/case-workspace/pathology/hooks/use-radial-tool-selector"
import {
  describeArcWithCaps,
  describeStrokeArc,
  INNER_R,
  MID_R,
  OUTER_R,
  polarToCartesian
} from "@frontend/pages/case-workspace/pathology/lib/arc-geometry"
import type { VectorGroup, VectorTool } from "@frontend/pages/case-workspace/pathology/types"
import { Brush, ChevronLeft, Eraser, PenTool, Redo2, Sparkles, Undo2 } from "lucide-react"
import { useCallback, useId, useRef, useState } from "react"

import { RadialLabelArc } from "./radial-label-arc"

interface RadialToolSelectorProps {
  containerRef: React.RefObject<HTMLDivElement | null>
  activeVectorTool: VectorTool | null
  vectorBrushRadiusPx: number
  vectorEraserRadiusPx: number
  onVectorToolChange: (tool: VectorTool | null) => void
  onVectorBrushRadiusChange: (radius: number) => void
  onVectorEraserRadiusChange: (radius: number) => void
  maxToolRadius: number
  onUndo: () => void
  onRedo: () => void
  enabled: boolean
  activeVectorGroups?: VectorGroup[]
  activeVectorLabelKey?: string | null
  onSelectLabel?: (labelKey: string) => void
}

// Arc geometry constants (derived from shared radii)
const BAND_W = OUTER_R - INNER_R // 70px band
const CAP_R = BAND_W / 2 // 35px rounded end cap radius
const ARC_SPAN_DEG = 90
const ARC_START_DEG = -20 // rotated CCW 20° from bottom-right
const ARC_END_DEG = ARC_START_DEG + ARC_SPAN_DEG
const BTN_SIZE = 46
const ICON_CLASS = "size-7 stroke-[2.5]"

// SVG viewBox: origin at top-left, arc extends down-right toward pointer
const VB_SIZE = OUTER_R + CAP_R + 8 // enough room for caps + padding
const CX = 0 // arc center at left edge of viewBox
const CY = 0 // arc center at top edge of viewBox

// Rendered pixel size of the SVG element
const SVG_PX = VB_SIZE

// Pre-computed constant paths (all inputs are module-level constants)
const ARC_BAND_PATH = describeArcWithCaps(CX, CY, INNER_R, OUTER_R, ARC_START_DEG, ARC_END_DEG)

// Pre-computed button positions (constant geometry)
const BUTTON_STEP_DEG = ARC_SPAN_DEG / 5
function computeButtonPos(offset: number) {
  const angle = ARC_START_DEG + offset * BUTTON_STEP_DEG
  return polarToCartesian(CX, CY, MID_R, angle)
}
const BTN_POS_0 = computeButtonPos(0)
const BTN_POS_1 = computeButtonPos(1)
const BTN_POS_2 = computeButtonPos(2)
const BTN_POS_3 = computeButtonPos(3)
const BTN_POS_4 = computeButtonPos(4)
const BTN_POS_5 = computeButtonPos(5)

interface ArcButtonProps {
  cx: number
  cy: number
  icon: React.ComponentType<{ className?: string }>
  active?: boolean
  label: string
  shortcut?: string
  onClick: () => void
}

function ArcButton({ cx, cy, icon: Icon, active, label, shortcut, onClick }: ArcButtonProps) {
  return (
    <foreignObject x={cx - BTN_SIZE / 2} y={cy - BTN_SIZE / 2} width={BTN_SIZE} height={BTN_SIZE}>
      <button
        onClick={onClick}
        title={`${label}${shortcut ? ` (${shortcut})` : ""}`}
        className={`w-full h-full rounded-full flex items-center justify-center transition-colors ${
          active
            ? "bg-primary text-primary-foreground ring-2 ring-primary/50"
            : "text-foreground/80 hover:bg-accent/60 hover:text-foreground"
        }`}
      >
        <Icon className={ICON_CLASS} />
      </button>
    </foreignObject>
  )
}

export function RadialToolSelector({
  containerRef,
  activeVectorTool,
  vectorBrushRadiusPx,
  vectorEraserRadiusPx,
  onVectorToolChange,
  onVectorBrushRadiusChange,
  onVectorEraserRadiusChange,
  maxToolRadius,
  onUndo,
  onRedo,
  enabled,
  activeVectorGroups,
  activeVectorLabelKey,
  onSelectLabel
}: RadialToolSelectorProps) {
  const { isOpen, view, setView, anchor, close, selectorRef } = useRadialToolSelector({
    containerRef,
    enabled,
    onDismissWithTool: onVectorToolChange
  })

  const uid = useId()
  const shadowId = `arc-shadow-${uid}`

  const handleToolClick = useCallback(
    (tool: VectorTool) => {
      if (tool === "sam" || tool === "pen") {
        onVectorToolChange(tool)
        return
      }
      if (activeVectorTool === tool) {
        setView("settings")
      } else {
        onVectorToolChange(tool)
      }
    },
    [activeVectorTool, onVectorToolChange, setView]
  )

  if (!isOpen) return null

  const items: ArcButtonProps[] = [
    { cx: BTN_POS_0.x, cy: BTN_POS_0.y, icon: Undo2, label: "Undo", shortcut: "⌘Z", onClick: onUndo },
    { cx: BTN_POS_1.x, cy: BTN_POS_1.y, icon: Redo2, label: "Redo", shortcut: "⌘⇧Z", onClick: onRedo },
    {
      cx: BTN_POS_2.x,
      cy: BTN_POS_2.y,
      icon: PenTool,
      label: "Pen",
      shortcut: "P",
      active: activeVectorTool === "pen",
      onClick: () => handleToolClick("pen")
    },
    {
      cx: BTN_POS_3.x,
      cy: BTN_POS_3.y,
      icon: Brush,
      label: "Brush",
      shortcut: "B",
      active: activeVectorTool === "brush",
      onClick: () => handleToolClick("brush")
    },
    {
      cx: BTN_POS_4.x,
      cy: BTN_POS_4.y,
      icon: Eraser,
      label: "Erase",
      shortcut: "E",
      active: activeVectorTool === "erase",
      onClick: () => handleToolClick("erase")
    },
    {
      cx: BTN_POS_5.x,
      cy: BTN_POS_5.y,
      icon: Sparkles,
      label: "SAM",
      shortcut: "S",
      active: activeVectorTool === "sam",
      onClick: () => handleToolClick("sam")
    }
  ]

  return (
    <div
      ref={selectorRef}
      className="absolute z-50 pointer-events-auto"
      style={{ left: anchor.x, top: anchor.y }}
      onPointerDown={(e) => e.stopPropagation()}
    >
      {/* Arc view */}
      <div
        className={`transition-opacity duration-150 ${
          view === "arc" ? "opacity-100" : "opacity-0 pointer-events-none absolute inset-0"
        }`}
      >
        <svg
          width={SVG_PX}
          height={SVG_PX}
          viewBox={`0 0 ${VB_SIZE * 2} ${VB_SIZE * 2}`}
          className="overflow-visible animate-in fade-in-0 zoom-in-95 duration-150"
        >
          <defs>
            <filter id={shadowId} x="-10%" y="-10%" width="120%" height="120%">
              <feDropShadow dx="0" dy="2" stdDeviation="4" floodColor="black" floodOpacity="0.35" />
            </filter>
          </defs>

          <path
            d={ARC_BAND_PATH}
            fill="hsl(var(--card) / 0.92)"
            stroke="hsl(var(--border) / 0.4)"
            strokeWidth={1}
            filter={`url(#${shadowId})`}
          />

          {items.map((btn) => (
            <ArcButton key={btn.label} {...btn} />
          ))}
        </svg>
      </div>

      {/* Settings view — arc slider */}
      <div
        className={`transition-opacity duration-150 ${
          view === "settings" ? "opacity-100" : "opacity-0 pointer-events-none absolute inset-0"
        }`}
      >
        {view === "settings" && (activeVectorTool === "brush" || activeVectorTool === "erase") && (
          <ArcSliderPanel
            uid={uid}
            tool={activeVectorTool}
            maxToolRadius={maxToolRadius}
            radiusPx={activeVectorTool === "brush" ? vectorBrushRadiusPx : vectorEraserRadiusPx}
            onRadiusChange={activeVectorTool === "brush" ? onVectorBrushRadiusChange : onVectorEraserRadiusChange}
            onBack={() => setView("arc")}
          />
        )}
      </div>

      {/* Label arc — mirrored in bottom-left quadrant */}
      {activeVectorGroups && activeVectorGroups.length > 0 && onSelectLabel && (
        <RadialLabelArc
          activeVectorGroups={activeVectorGroups}
          activeVectorLabelKey={activeVectorLabelKey ?? null}
          onSelectLabel={(key) => {
            onSelectLabel(key)
            close()
          }}
        />
      )}
    </div>
  )
}

// ── Arc Slider Panel ──────────────────────────────────────────────

// Slider track sits slightly inset from the main band
const TRACK_R = MID_R
const SLIDER_STROKE = BAND_W * 0.45 // track and filled track thickness

// Reserve space for the back button at the start of the arc
const SLIDER_PAD_DEG = 18 // degrees reserved for back button
const SLIDER_START_DEG = ARC_START_DEG + SLIDER_PAD_DEG
const SLIDER_SPAN_DEG = ARC_END_DEG - SLIDER_START_DEG

// Thumb size range (viewBox units): small at min, large at max
const THUMB_MIN_R = 8
const THUMB_MAX_R = 24

// Pre-computed constant paths for slider
const SLIDER_BAND_PATH = ARC_BAND_PATH // same shape as main arc
const SLIDER_TRACK_BG_PATH = describeStrokeArc(CX, CY, TRACK_R, SLIDER_START_DEG, ARC_END_DEG)

// Back button position (constant)
const BACK_BTN_POS = polarToCartesian(CX, CY, MID_R, ARC_START_DEG)

// Label position (constant)
const LABEL_POS = polarToCartesian(CX, CY, INNER_R * 0.45, ARC_START_DEG + ARC_SPAN_DEG * 0.5)

function ArcSliderPanel({
  uid,
  tool,
  maxToolRadius,
  radiusPx,
  onRadiusChange,
  onBack
}: {
  uid: string
  tool: "brush" | "erase"
  maxToolRadius: number
  radiusPx: number
  onRadiusChange: (v: number) => void
  onBack: () => void
}) {
  const svgRef = useRef<SVGSVGElement>(null)
  const [isDragging, setIsDragging] = useState(false)
  const sliderShadowId = `arc-slider-shadow-${uid}`

  const min = tool === "brush" ? 2 : 1
  const max = maxToolRadius

  const fraction = Math.max(0, Math.min(1, (radiusPx - min) / (max - min)))
  const currentAngle = SLIDER_START_DEG + fraction * SLIDER_SPAN_DEG

  // Thumb radius scales with value
  const thumbR = THUMB_MIN_R + fraction * (THUMB_MAX_R - THUMB_MIN_R)
  const thumbPos = polarToCartesian(CX, CY, TRACK_R, currentAngle)

  // Filled portion of track (start to current)
  const trackFilledPath =
    currentAngle > SLIDER_START_DEG + 0.5 ? describeStrokeArc(CX, CY, TRACK_R, SLIDER_START_DEG, currentAngle) : ""

  // Convert client coords to arc angle
  const clientToAngle = useCallback((clientX: number, clientY: number): number => {
    const svg = svgRef.current
    if (!svg) return ARC_START_DEG
    const rect = svg.getBoundingClientRect()
    const scale = (VB_SIZE * 2) / rect.width
    const svgX = (clientX - rect.left) * scale
    const svgY = (clientY - rect.top) * scale
    const dx = svgX - CX
    const dy = svgY - CY
    let angle = Math.atan2(dy, dx) * (180 / Math.PI) + 90
    if (angle < 0) angle += 360
    if (angle > ARC_END_DEG) {
      const distToStart = Math.min(Math.abs(angle - SLIDER_START_DEG), Math.abs(angle - 360 - SLIDER_START_DEG))
      const distToEnd = Math.abs(angle - ARC_END_DEG)
      return distToStart < distToEnd ? SLIDER_START_DEG : ARC_END_DEG
    }
    return Math.max(SLIDER_START_DEG, angle)
  }, [])

  const angleToValue = useCallback(
    (angle: number) => {
      const frac = (angle - SLIDER_START_DEG) / SLIDER_SPAN_DEG
      return Math.max(min, Math.min(max, Math.round(min + frac * (max - min))))
    },
    [min, max]
  )

  const handlePointerDown = useCallback(
    (e: React.PointerEvent<SVGSVGElement>) => {
      e.preventDefault()
      svgRef.current?.setPointerCapture(e.pointerId)
      setIsDragging(true)
      const angle = clientToAngle(e.clientX, e.clientY)
      onRadiusChange(angleToValue(angle))
    },
    [clientToAngle, angleToValue, onRadiusChange]
  )

  const handlePointerMove = useCallback(
    (e: React.PointerEvent<SVGSVGElement>) => {
      if (!isDragging) return
      const angle = clientToAngle(e.clientX, e.clientY)
      onRadiusChange(angleToValue(angle))
    },
    [isDragging, clientToAngle, angleToValue, onRadiusChange]
  )

  const handlePointerUp = useCallback(() => {
    setIsDragging(false)
  }, [])

  const toolName = tool === "brush" ? "Brush" : "Eraser"

  return (
    <div className="relative" style={{ width: SVG_PX, height: SVG_PX }}>
      <svg
        ref={svgRef}
        width={SVG_PX}
        height={SVG_PX}
        viewBox={`0 0 ${VB_SIZE * 2} ${VB_SIZE * 2}`}
        className="overflow-visible animate-in fade-in-0 zoom-in-95 duration-150 touch-none"
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
      >
        <defs>
          <filter id={sliderShadowId} x="-10%" y="-10%" width="120%" height="120%">
            <feDropShadow dx="0" dy="2" stdDeviation="4" floodColor="black" floodOpacity="0.35" />
          </filter>
        </defs>

        {/* Band background */}
        <path
          d={SLIDER_BAND_PATH}
          fill="hsl(var(--card) / 0.92)"
          stroke="hsl(var(--border) / 0.4)"
          strokeWidth={1}
          filter={`url(#${sliderShadowId})`}
        />

        {/* Track background */}
        <path
          d={SLIDER_TRACK_BG_PATH}
          fill="none"
          stroke="hsl(var(--muted) / 0.5)"
          strokeWidth={SLIDER_STROKE}
          strokeLinecap="round"
        />

        {/* Filled track */}
        {trackFilledPath && (
          <path
            d={trackFilledPath}
            fill="none"
            stroke="hsl(var(--primary) / 0.7)"
            strokeWidth={SLIDER_STROKE}
            strokeLinecap="round"
          />
        )}

        {/* Thumb — circle that grows with value */}
        <circle
          cx={thumbPos.x}
          cy={thumbPos.y}
          r={thumbR}
          fill="white"
          stroke="hsl(var(--primary))"
          strokeWidth={2.5}
          className="drop-shadow-sm"
          style={{ cursor: isDragging ? "grabbing" : "grab" }}
        />

        {/* Tool name + value inside the arc hole */}
        <text
          x={LABEL_POS.x}
          y={LABEL_POS.y - 12}
          textAnchor="middle"
          dominantBaseline="central"
          fill="white"
          fontSize={17}
          fontWeight={600}
          fontFamily="system-ui, sans-serif"
          stroke="black"
          strokeWidth={3}
          paintOrder="stroke"
          style={{ pointerEvents: "none" }}
        >
          {toolName}
        </text>
        <text
          x={LABEL_POS.x}
          y={LABEL_POS.y + 14}
          textAnchor="middle"
          dominantBaseline="central"
          fill="white"
          fontSize={20}
          fontWeight={700}
          fontFamily="ui-monospace, monospace"
          stroke="black"
          strokeWidth={3}
          paintOrder="stroke"
          style={{ pointerEvents: "none" }}
        >
          {Math.round(radiusPx)} px
        </text>

        {/* Back button — on the arc at the start position */}
        <foreignObject
          x={BACK_BTN_POS.x - BTN_SIZE / 2}
          y={BACK_BTN_POS.y - BTN_SIZE / 2}
          width={BTN_SIZE}
          height={BTN_SIZE}
        >
          <button
            onClick={(e) => {
              e.stopPropagation()
              onBack()
            }}
            onPointerDown={(e) => e.stopPropagation()}
            className="w-full h-full rounded-full flex items-center justify-center hover:bg-accent/60 transition-colors text-foreground"
            title="Back"
          >
            <ChevronLeft className="size-7 stroke-[2.5]" />
          </button>
        </foreignObject>
      </svg>
    </div>
  )
}
